import React, { memo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { BounceLoader } from "react-spinners";
import OtpInput from "react-otp-input";
import { useDispatch } from "react-redux";
import { useResendOtpLoginCodeMutation, useValidateOtpLoginCodeMutation } from "../../../../service/authentication-service";
import styles from "./OneTimePasswordForm.module.scss";
import { logIn } from "../../../../stores/slices/current-user-slice";
import { useLocation, useNavigate } from "react-router-dom";
import { ApplicationRoutes } from "../../../../routes";
import { UserType } from "../../../../model/userType";
import { setFirstLogin } from "../../../../stores/slices/first-login-slice";
import TimerButton from "../../../../common/components/timer-button/TimerButton";
import { useTheme } from "../../../../theme/useTheme";
import { getUserIdFromToken } from "../../../../guards/common/authentification-functions";
import { describeError, trackLoginFailed, trackLoginOtpResent, trackLoginSuccess } from "../../../../services/business-analytics/authEvents";

type Props = {
	email: string;
	onChangeEmail: () => void;
	otpToken: string;
};

const AdminOneTimePasswordForm: React.FC<Props> = memo((props: Props) => {
	const location = useLocation();
	const goBackRoute: string | undefined = location.state ? location.state.route : null;

	const otpLength = 6;
	const [errorMessage, setErrorMessage] = useState("");

	const { email, onChangeEmail /*, otpToken */ } = props;
	const [validateOtpLoginCode, { isLoading }] = useValidateOtpLoginCodeMutation();
	const [resendOtpLoginCode] = useResendOtpLoginCodeMutation();

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { setPreference } = useTheme();

	const [otp, setOtp] = useState("");
	const [isSubmitted, setIsSubmitted] = useState(false);
	const resendCount = useRef(0);

	const validateOtp = (otp: string) => {
		if (otp.length === otpLength) {
			setErrorMessage("");
			return true;
		} else {
			setErrorMessage("Please enter the complete OTP");
			return false;
		}
	};

	const handleVerify = async (otp: string) => {
		try {
			setIsSubmitted(true);
			if (!validateOtp(otp)) {
				return;
			}
			const response = await validateOtpLoginCode({ email, OTPLoginCode: otp }).unwrap();
			console.log("OTP validation response:", response);
			dispatch(
				logIn({
					token: response.token,
					userType: response.userType,
					refreshToken: response.refreshToken,
				})
			);
			trackLoginSuccess("password", getUserIdFromToken(response.token));

			const userType = response.userType;
			let route = "";

			switch (userType) {
				case UserType.BPSAdmin:
					dispatch(setFirstLogin(false));
					setPreference("light");
					// Straight to the dashboard — the module picker at ADMIN_LANDING is skipped for now.
					route = `/${ApplicationRoutes.ADMIN_BASE}/${ApplicationRoutes.DASHBOARD}`;
					break;
				case UserType.Utility:
					dispatch(setFirstLogin(false));
					setPreference("light");
					route = `/${ApplicationRoutes.ADMIN_UTIL_BASE}/${ApplicationRoutes.ADMIN_LANDING}`;
					break;
				case UserType.Provider:
					route = `/${ApplicationRoutes.PROVIDER_BASE}/${ApplicationRoutes.DASHBOARD}`;
					dispatch(setFirstLogin(false));
					break;
				case UserType.Customer:
					route = `/${ApplicationRoutes.CUSTOMER_NEXT_STEP}`;
					break;
				case UserType.Lender:
					break;
			}

			navigate(goBackRoute ?? route);
		} catch (error: any) {
			trackLoginFailed("password", describeError(error));
			toast.error(error.data?.title || "Failed to validate OTP. Please try again.");
		}
	};

	const handleChange = (otp: string) => {
		setOtp(otp);
		if (isSubmitted) {
			validateOtp(otp);
		}
		if (otp.length === otpLength) {
			handleVerify(otp);
		}
	};

	const handleVerifyButton = () => {
		handleVerify(otp);
	};

	const handleResendOtp = async () => {
		try {
			await resendOtpLoginCode({ email }).unwrap();
			resendCount.current += 1;
			trackLoginOtpResent("email", resendCount.current);
			toast.success("OTP sent successfully");
		} catch (error: any) {
			toast.error(error.data?.title || "Failed to resend OTP. Please try again.");
		}
	};

	return (
		<div className={styles.container}>
			<div className={styles.header}>
				OTP
				<div className={`link`}>Need Help?</div>
			</div>
			<div className={styles.contentContainer}>
				<div className={styles.inputLabel}>
					Enter the code we've sent by mail to {email}
					<button className={`standardButton ${styles.changeEmailButton}`} onClick={onChangeEmail}>
						Change mail
					</button>
				</div>
				<OtpInput value={otp} onChange={handleChange} numInputs={otpLength} containerStyle={styles.otpContainer} renderInput={props => <input {...props} />} />
				{errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
				<button className={`standardButton ${styles.verifyButton}`} onClick={handleVerifyButton}>
					{isLoading ? <BounceLoader color="#DE841B" size={25} /> : "Verify"}
				</button>
				<p>
					<b>Important: </b>If you do not receive the email in your inbox, please check your spam or junk folder as it may have been filtered by your email provider.
				</p>
				<div className={styles.resendContainer}>
					Didn't receive the code? <TimerButton timerLength={60} onClick={handleResendOtp} />
				</div>
			</div>
		</div>
	);
});

export default AdminOneTimePasswordForm;
